// Display/charge currency the shopper picked from the top bar (like Amazon's
// currency setting). Prices come from the API in USD; when SGD is selected we
// convert them here and Stripe charges the converted amount in SGD.
import { useEffect, useState } from "react";

// 1 USD = USD_TO_SGD SGD. Update this when the rate moves.
export const USD_TO_SGD = 1.3;

export const CURRENCIES = {
  USD: { code: "USD", symbol: "US$", label: "USD - US Dollar", rate: 1 },
  SGD: { code: "SGD", symbol: "S$", label: "SGD - Singapore Dollar", rate: USD_TO_SGD },
};

// Live Stripe (StripePayment.jsx) always creates USD payment intents, so currency
// selection stays off in production: every price is shown and charged in USD.
// Flip this only once StripePayment charges in the selected currency.
export const CURRENCY_SELECTION_ENABLED = false;

const STORAGE_KEY = "currency";
const CHANGE_EVENT = "currencyChanged";

export const getCurrency = () => {
  if (!CURRENCY_SELECTION_ENABLED) return "USD";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return CURRENCIES[stored] ? stored : "USD";
  } catch {
    return "USD";
  }
};

export const setCurrency = (code) => {
  if (!CURRENCIES[code]) return;
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Storage blocked — the choice still applies until the page reloads.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: code }));
};

// Converts a USD amount into `code`, rounded to cents. Convert each line before
// summing so the totals shown always add up to what Stripe charges.
export const convertFromUSD = (usd, code = getCurrency()) => {
  const rate = (CURRENCIES[code] || CURRENCIES.USD).rate;
  return Math.round((Number(usd) || 0) * rate * 100) / 100;
};

// "S$12.99" — `amount` must already be in `code`.
export const formatMoney = (amount, code = getCurrency()) =>
  `${(CURRENCIES[code] || CURRENCIES.USD).symbol}${(Number(amount) || 0).toFixed(2)}`;

export const useCurrency = () => {
  const [currency, setCurrencyState] = useState(getCurrency);

  useEffect(() => {
    const onChange = () => setCurrencyState(getCurrency());
    const onStorage = (e) => { if (e.key === STORAGE_KEY) onChange(); };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return {
    currency,
    setCurrency,
    convert: (usd) => convertFromUSD(usd, currency),
    format: (amount) => formatMoney(amount, currency),
  };
};
